// src/components/AccessRequestModal.tsx
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonButtons,
  IonIcon,
  IonSpinner,
  useIonToast,
  IonInput,
  IonItem,
  IonLabel,
  IonText,
  IonTextarea,
} from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import React, { useState } from 'react';
import axios from 'axios';

interface AccessRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  shareToken: string;
  profileTitle?: string;
}

const AccessRequestModal: React.FC<AccessRequestModalProps> = ({
  isOpen,
  onClose,
  shareToken,
  profileTitle
}) => {
  const [present] = useIonToast();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSendRequest = async () => {
    if (!email || !email.includes('@')) {
      present({ message: 'Please enter a valid email address', duration: 3000, color: 'danger' });
      return;
    }

    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.autotuneresume.com/api/v1';
      await axios.post(`${apiUrl}/public/profile/${shareToken}/request-permission`, {
        email: email,
        message: message
      });

      present({ message: 'Access request sent successfully!', duration: 3000, color: 'success' });
      setSubmitted(true);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to send access request';
      present({ message: errorMessage, duration: 3000, color: 'danger' });
    } finally {
      setLoading(false);
    }
  };

  const handleModalDismiss = () => {
    setEmail('');
    setMessage('');
    setSubmitted(false);
    onClose();
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={handleModalDismiss}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Request Access</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleModalDismiss}>
              <IonIcon icon={closeOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <IonSpinner />
          </div>
        ) : submitted ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <IonText>
              <h2>Request Sent!</h2>
              <p>
                Your access request has been sent to the profile owner.
                You'll be notified by email when they respond.
              </p>
            </IonText>
            <IonButton expand="block" onClick={handleModalDismiss} style={{ marginTop: '24px' }}>
              Close
            </IonButton>
          </div>
        ) : (
          <div>
            <IonText>
              <h2>Request Access</h2>
              <p>
                {profileTitle ? `"${profileTitle}"` : 'This profile'} requires permission from the owner to access.
              </p>
            </IonText>

            <IonItem style={{ marginTop: '24px' }}>
              <IonLabel position="stacked">Your Email</IonLabel>
              <IonInput
                type="email"
                value={email}
                placeholder="Enter your email address"
                onIonInput={(e) => setEmail(e.detail.value!)}
              />
            </IonItem>

            <IonItem style={{ marginTop: '16px' }}>
              <IonLabel position="stacked">Message (Optional)</IonLabel>
              <IonTextarea
                value={message}
                placeholder="I'm interested in the Senior Engineer position..."
                rows={4}
                maxlength={500}
                onIonInput={(e) => setMessage(e.detail.value!)}
              />
            </IonItem>

            <IonText>
              <p style={{ fontSize: '0.9rem', color: 'var(--app-text-color-secondary)', marginTop: '16px' }}>
                The profile owner will receive your request and can approve or deny access.
              </p>
            </IonText>

            <IonButton
              expand="block"
              onClick={handleSendRequest}
              style={{ marginTop: '24px' }}
              disabled={!email || loading}
            >
              Send Request
            </IonButton>
          </div>
        )}
      </IonContent>
    </IonModal>
  );
};

export default AccessRequestModal;